import React from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { FaCalendarAlt, FaUser, FaTags, FaClock, FaArrowLeft, FaFacebookF, FaTwitter, FaLinkedinIn, FaRedditAlien } from 'react-icons/fa';
import './BlogPost.css';

// Sample blog post data - in a real app, this would come from an API based on the post ID
const blogPostData = {
  id: 1,
  title: 'Getting Started with React and FastAPI',
  excerpt: 'Learn how to build a full-stack application using React for the frontend and FastAPI for the backend.',
  content: `
    <p>In this comprehensive guide, we'll walk through building a modern full-stack application using React for the frontend and FastAPI for the backend. This powerful combination allows for rapid development with a great developer experience while maintaining high performance.</p>

    <h2>Why React and FastAPI?</h2>

    <p>React has established itself as one of the most popular frontend libraries, offering a component-based architecture that makes building interactive UIs a breeze. Its virtual DOM implementation ensures efficient updates and renders, while the vast ecosystem provides solutions for almost any frontend challenge.</p>

    <p>FastAPI, on the other hand, is a relatively new but incredibly powerful Python web framework. It offers several advantages:</p>

    <ul>
      <li>High performance: Built on Starlette and Pydantic, FastAPI is one of the fastest Python frameworks available</li>
      <li>Automatic API documentation: OpenAPI and Swagger UI integration out of the box</li>
      <li>Type hints and validation: Leverages Python's type hints for request validation and editor support</li>
      <li>Async support: First-class support for async/await syntax</li>
    </ul>

    <p>Together, these technologies form a powerful stack for building modern web applications.</p>

    <h2>Setting Up the Project</h2>

    <p>Let's start by setting up our project structure. We'll create a directory for our application and set up both the frontend and backend components.</p>

    <pre><code>mkdir react-fastapi-app
cd react-fastapi-app
mkdir frontend backend</code></pre>

    <h3>Setting Up the Frontend</h3>

    <p>For the frontend, we'll use Create React App to bootstrap our React application:</p>

    <pre><code>cd frontend
npx create-react-app .
npm install axios react-router-dom bootstrap react-bootstrap</code></pre>

    <p>This sets up a new React application and installs some useful dependencies:</p>

    <ul>
      <li>axios: For making HTTP requests to our API</li>
      <li>react-router-dom: For client-side routing</li>
      <li>bootstrap and react-bootstrap: For styling our application</li>
    </ul>

    <h3>Setting Up the Backend</h3>

    <p>For the backend, we'll create a virtual environment and install FastAPI along with Uvicorn, which will serve as our ASGI server:</p>

    <pre><code>cd ../backend
python -m venv venv
source venv/bin/activate  # On Windows, use: venv\\Scripts\\activate
pip install fastapi uvicorn sqlalchemy pydantic python-dotenv</code></pre>

    <h2>Creating the FastAPI Backend</h2>

    <p>Let's create a basic FastAPI application. Create a file named <code>main.py</code> in the backend directory:</p>

    <pre><code>from fastapi import FastAPI
from fastapi.middleware.cors import CORSMiddleware

app = FastAPI()

# Configure CORS
app.add_middleware(
    CORSMiddleware,
    allow_origins=["http://localhost:3000"],
    allow_credentials=True,
    allow_methods=["*"],
    allow_headers=["*"],
)

@app.get("/api/hello")
async def hello():
    return {"message": "Hello from FastAPI!"}

if __name__ == "__main__":
    import uvicorn
    uvicorn.run(app, host="0.0.0.0", port=8000)</code></pre>

    <p>This creates a simple FastAPI application with a single endpoint that returns a greeting message. We've also configured CORS to allow requests from our React application running on port 3000.</p>

    <h2>Building the React Frontend</h2>

    <p>Now, let's create a simple React component that fetches data from our FastAPI backend. Open <code>src/App.js</code> and replace its contents with:</p>

    <pre><code>import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';

function App() {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get('http://localhost:8000/api/hello');
        setMessage(response.data.message);
        setError(null);
      } catch (err) {
        setError('Error fetching data from API');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card>
            <Card.Header as="h5">React + FastAPI Demo</Card.Header>
            <Card.Body>
              {loading ? (
                <p>Loading...</p>
              ) : error ? (
                <p className="text-danger">{error}</p>
              ) : (
                <>
                  <Card.Title>Message from API</Card.Title>
                  <Card.Text>{message}</Card.Text>
                  <Button variant="primary">Learn More</Button>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default App;</code></pre>

    <h2>Running the Application</h2>

    <p>Now, let's run both the frontend and backend applications:</p>

    <p>For the backend, navigate to the backend directory and run:</p>

    <pre><code>uvicorn main:app --reload</code></pre>

    <p>For the frontend, open a new terminal, navigate to the frontend directory, and run:</p>

    <pre><code>npm start</code></pre>

    <p>Now, if you open your browser and navigate to <code>http://localhost:3000</code>, you should see your React application displaying the message from the FastAPI backend.</p>

    <h2>Next Steps</h2>

    <p>This is just the beginning of what you can build with React and FastAPI. Here are some ideas for expanding this application:</p>

    <ul>
      <li>Add a database using SQLAlchemy with FastAPI</li>
      <li>Implement user authentication</li>
      <li>Create a more complex React application with multiple components and routes</li>
      <li>Add form validation with Formik or React Hook Form</li>
      <li>Implement state management with Redux or Context API</li>
    </ul>

    <p>The combination of React and FastAPI provides a solid foundation for building modern web applications with a great developer experience and high performance.</p>
  `,
  date: 'May 15, 2023',
  author: 'Rahul Bhoyar',
  authorImage: '/images/author.jpg',
  category: 'Web Development',
  tags: ['React', 'FastAPI', 'Full Stack'],
  image: '/images/blog/react-fastapi.jpg',
  readTime: '12 min read',
  relatedPosts: [
    {
      id: 3,
      title: 'Containerizing Your Applications with Docker',
      excerpt: 'A comprehensive guide to containerizing your applications using Docker and Docker Compose.',
      image: '/images/blog/docker-containers.jpg',
      date: 'July 10, 2023',
      author: 'Rahul Bhoyar'
    },
    {
      id: 5,
      title: 'Building RESTful APIs with FastAPI',
      excerpt: 'Learn how to build robust and high-performance RESTful APIs using FastAPI and Python.',
      image: '/images/blog/fastapi.jpg',
      date: 'September 12, 2023',
      author: 'Rahul Bhoyar'
    },
    {
      id: 6,
      title: 'Modern CSS Techniques',
      excerpt: 'Discover modern CSS techniques and best practices for building responsive and beautiful websites.',
      image: '/images/blog/modern-css.jpg',
      date: 'October 8, 2023',
      author: 'Rahul Bhoyar'
    }
  ]
};

const BlogPost = () => {
  // In a real app, you would use the ID from the URL to fetch the specific blog post
  const { id } = useParams();
  const post = blogPostData; // In a real app, this would be fetched based on the ID

  return (
    <div className="blog-post-page">
      <div className="blog-post-hero" style={{ backgroundImage: `url(${process.env.PUBLIC_URL + post.image})` }}>
        <div className="blog-post-hero-overlay">
          <Container>
            <Row className="justify-content-center">
              <Col lg={8} className="text-center">
                <Link to="/blog" className="back-to-blog">
                  <FaArrowLeft /> Back to Blog
                </Link>
                <h1 className="blog-post-title">{post.title}</h1>
                <div className="blog-post-meta">
                  <span><FaCalendarAlt /> {post.date}</span>
                  <span><FaUser /> {post.author}</span>
                  <span><FaClock /> {post.readTime}</span>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </div>

      <Container className="blog-post-container">
        <Row>
          <Col lg={8}>
            <article className="blog-post-content text-left">
              <div dangerouslySetInnerHTML={{ __html: post.content }} />

              <div className="blog-post-tags">
                <FaTags />
                {post.tags.map((tag, index) => (
                  <Link key={index} to={`/blog/tag/${tag.toLowerCase().replace(' ', '-')}`} className="blog-post-tag">
                    {tag}
                  </Link>
                ))}
              </div>

              <div className="blog-post-share">
                <span>Share this post:</span>
                <div className="share-buttons">
                  <Button variant="outline-primary" className="share-button facebook">
                    <FaFacebookF />
                  </Button>
                  <Button variant="outline-primary" className="share-button twitter">
                    <FaTwitter />
                  </Button>
                  <Button variant="outline-primary" className="share-button linkedin">
                    <FaLinkedinIn />
                  </Button>
                  <Button variant="outline-primary" className="share-button reddit">
                    <FaRedditAlien />
                  </Button>
                </div>
              </div>

              <div className="blog-post-author">
                <div className="author-image" style={{ backgroundImage: `url(${process.env.PUBLIC_URL + post.authorImage})` }}></div>
                <div className="author-info text-left">
                  <h4>Written by {post.author}</h4>
                  <p>Rahul is a full-stack developer with expertise in React, FastAPI, and machine learning. He enjoys writing about web development, data science, and emerging technologies.</p>
                </div>
              </div>
            </article>
          </Col>

          <Col lg={4}>
            <div className="blog-post-sidebar text-left">
              <div className="sidebar-widget">
                <h4 className="widget-title">Related Posts</h4>
                <div className="related-posts">
                  {post.relatedPosts.map(relatedPost => (
                    <div key={relatedPost.id} className="related-post">
                      <Link to={`/blog/${relatedPost.id}`}>
                        <div className="related-post-image" style={{ backgroundImage: `url(${process.env.PUBLIC_URL + relatedPost.image})` }}></div>
                        <div className="related-post-info">
                          <h5>{relatedPost.title}</h5>
                          <span>{relatedPost.date}</span>
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>

              <div className="sidebar-widget">
                <h4 className="widget-title">Categories</h4>
                <ul className="categories-list">
                  <li><Link to="/blog/category/web-development">Web Development</Link></li>
                  <li><Link to="/blog/category/machine-learning">Machine Learning</Link></li>
                  <li><Link to="/blog/category/data-science">Data Science</Link></li>
                  <li><Link to="/blog/category/devops">DevOps</Link></li>
                </ul>
              </div>

              <div className="sidebar-widget">
                <h4 className="widget-title">Subscribe to Newsletter</h4>
                <p>Get the latest posts delivered straight to your inbox.</p>
                <form className="subscribe-form">
                  <input type="email" placeholder="Your email address" required />
                  <Button variant="primary" type="submit" className="w-100 mt-2">Subscribe</Button>
                </form>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default BlogPost;
